import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, X, ChevronDown, ChevronUp, ImageIcon, ZoomIn } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { useToast } from '../shared/components/Toast.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { formatValuta, formatDatum } from '../shared/utils/formatters.js';

interface RegelData {
  id: string;
  categorie: string;
  omschrijving: string;
  toelichting: string | null;
  bedrag: number;
  status: string;
  fotoUrls: string[];
}

interface VorderingsstaatDetail {
  id: string;
  dossierId: string;
  leverDatum: string;
  status: string;
  subtotaal: number;
  btwPercentage: number;
  btwBedrag: number;
  totaal: number;
  opmerking: string | null;
  dossier: { id: string; dossierNummer: string; klantNaam: string; entityId: string };
  regels: RegelData[];
}

const statusBadgeConfig: Record<string, { bg: string; text: string; label: string }> = {
  INGEDIEND: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Ingediend' },
  TER_GOEDKEURING: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Ter goedkeuring' },
  GOEDGEKEURD: { bg: 'bg-green-100', text: 'text-green-700', label: 'Goedgekeurd' },
  BETWIST: { bg: 'bg-red-100', text: 'text-red-700', label: 'Betwist' },
};

export function VorderingsstaatBeoordelen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Local optimistic state for regel statuses
  const [localRegels, setLocalRegels] = useState<Record<string, { status: string; toelichting?: string }>>({});
  const [expandedRegel, setExpandedRegel] = useState<string | null>(null);
  const [betwistInput, setBetwistInput] = useState<Record<string, string>>({});
  const [showBetwistInput, setShowBetwistInput] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['vorderingsstaat-beoordelen', id],
    queryFn: () => apiFetch<VorderingsstaatDetail>(`/api/buitendienst/vorderingsstaten/${id}`),
    enabled: !!id,
  });

  // Sync server data to local state on load
  useEffect(() => {
    if (data?.regels) {
      setLocalRegels(prev => {
        const next: Record<string, { status: string; toelichting?: string }> = {};
        for (const r of data.regels) {
          next[r.id] = prev[r.id] || { status: r.status, toelichting: r.toelichting || undefined };
        }
        return next;
      });
    }
  }, [data]);

  // Auto-expand first PENDING regel on load
  useEffect(() => {
    if (data?.regels && !expandedRegel) {
      const firstPending = data.regels.find(r => (localRegels[r.id]?.status || r.status) === 'PENDING');
      if (firstPending) setExpandedRegel(firstPending.id);
    }
  }, [data, localRegels, expandedRegel]);

  const advanceToNextPending = useCallback((currentId: string) => {
    if (!data?.regels) return;
    const currentIndex = data.regels.findIndex(r => r.id === currentId);
    // Find next PENDING after current
    for (let i = currentIndex + 1; i < data.regels.length; i++) {
      const status = localRegels[data.regels[i].id]?.status || data.regels[i].status;
      if (status === 'PENDING') {
        setExpandedRegel(data.regels[i].id);
        return;
      }
    }
    // Wrap around: find first PENDING before current
    for (let i = 0; i < currentIndex; i++) {
      const status = localRegels[data.regels[i].id]?.status || data.regels[i].status;
      if (status === 'PENDING') {
        setExpandedRegel(data.regels[i].id);
        return;
      }
    }
    // No more PENDING — collapse
    setExpandedRegel(null);
  }, [data, localRegels]);

  const regelMutation = useMutation({
    mutationFn: ({ regelId, status, toelichting }: { regelId: string; status: string; toelichting?: string }) =>
      apiFetch(`/api/buitendienst/vorderingsstaten/${id}/regels/${regelId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, ...(toelichting ? { toelichting } : {}) }),
      }),
    onSuccess: (_result, vars) => {
      queryClient.invalidateQueries({ queryKey: ['vorderingsstaat-beoordelen', id] });
      advanceToNextPending(vars.regelId);
      setShowBetwistInput(null);
    },
    onError: (_err, vars) => {
      // Rollback optimistic update
      if (data) {
        const original = data.regels.find(r => r.id === vars.regelId);
        if (original) {
          setLocalRegels(prev => ({ ...prev, [vars.regelId]: { status: original.status, toelichting: original.toelichting || undefined } }));
        }
      }
      toast('error', 'Fout bij beoordelen regel');
    },
  });

  const goedkeurenMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/buitendienst/vorderingsstaten/${id}/goedkeuren`, { method: 'PATCH' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossier-volledig'] });
      queryClient.invalidateQueries({ queryKey: ['winkel-dashboard'] });
      toast('success', 'Vorderingsstaat goedgekeurd');
      if (data) navigate(`/dossiers/${data.dossierId}`);
    },
    onError: () => {
      toast('error', 'Fout bij goedkeuren vorderingsstaat');
    },
  });

  const betwistenMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/buitendienst/vorderingsstaten/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'BETWIST' }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossier-volledig'] });
      queryClient.invalidateQueries({ queryKey: ['winkel-dashboard'] });
      toast('info', 'Vorderingsstaat betwist — WMS is op de hoogte gebracht');
      if (data) navigate(`/dossiers/${data.dossierId}`);
    },
    onError: () => {
      toast('error', 'Fout bij betwisten vorderingsstaat');
    },
  });

  const handleGoedkeuren = (regelId: string) => {
    setLocalRegels(prev => ({ ...prev, [regelId]: { status: 'GOEDGEKEURD' } }));
    regelMutation.mutate({ regelId, status: 'GOEDGEKEURD' });
  };

  const handleBetwistStart = (regelId: string) => {
    setShowBetwistInput(regelId);
  };

  const handleBetwistConfirm = (regelId: string) => {
    const toelichting = betwistInput[regelId]?.trim() || '';
    setLocalRegels(prev => ({ ...prev, [regelId]: { status: 'BETWIST', toelichting: toelichting || undefined } }));
    regelMutation.mutate({ regelId, status: 'BETWIST', toelichting: toelichting || undefined });
    setBetwistInput(prev => ({ ...prev, [regelId]: '' }));
  };

  // Escape key closes lightbox
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxImages([]);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (isLoading) return (
    <div className="flex justify-center py-12">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data) return <div className="text-center py-12 text-gray-500">Vorderingsstaat niet gevonden</div>;

  // Use local state merged with server data
  const regels = data.regels.map(r => ({
    ...r,
    status: localRegels[r.id]?.status || r.status,
    toelichting: localRegels[r.id]?.toelichting || r.toelichting,
  }));

  const goedgekeurdCount = regels.filter(r => r.status === 'GOEDGEKEURD').length;
  const betwistCount = regels.filter(r => r.status === 'BETWIST').length;
  const pendingCount = regels.filter(r => r.status === 'PENDING').length;
  const beoordeeld = goedgekeurdCount + betwistCount;

  const goedgekeurdBedrag = regels.filter(r => r.status === 'GOEDGEKEURD').reduce((s, r) => s + r.bedrag, 0);
  const betwistBedrag = regels.filter(r => r.status === 'BETWIST').reduce((s, r) => s + r.bedrag, 0);
  const pendingBedrag = regels.filter(r => r.status === 'PENDING').reduce((s, r) => s + r.bedrag, 0);

  const alleBeoordeeld = pendingCount === 0 && regels.length > 0;
  const alleGoedgekeurd = alleBeoordeeld && betwistCount === 0;
  const isAfgerond = data.status === 'GOEDGEKEURD' || data.status === 'BETWIST';

  const badge = statusBadgeConfig[data.status] || statusBadgeConfig.TER_GOEDKEURING;

  return (
    <div className="max-w-3xl mx-auto pb-32">
      {/* Header */}
      <button onClick={() => navigate(`/dossiers/${data.dossierId}`)} className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft size={16} /> Terug naar dossier
      </button>

      <Card className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">Vorderingsstaat</h1>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{data.dossier.dossierNummer} — {data.dossier.klantNaam}</p>
            <p className="text-sm text-gray-500 mt-0.5">Leverdatum: {formatDatum(data.leverDatum)}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{formatValuta(data.totaal)}</p>
            <p className="text-xs text-gray-400">excl. BTW {formatValuta(data.subtotaal)}</p>
          </div>
        </div>

        {data.opmerking && (
          <p className="text-sm text-gray-600 italic mt-3">"{data.opmerking}"</p>
        )}

        {/* Progress bar — gold */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">{beoordeeld} van {regels.length} regels beoordeeld</span>
            {alleBeoordeeld && !isAfgerond && (
              <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Klaar voor afronden</span>
            )}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gold rounded-full transition-all"
              style={{ width: `${regels.length > 0 ? (beoordeeld / regels.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Regels */}
      <div className="space-y-3">
        {regels.map((regel) => {
          const isExpanded = expandedRegel === regel.id;
          const heeftFotos = regel.fotoUrls && regel.fotoUrls.length > 0;
          const isBetwistInputOpen = showBetwistInput === regel.id;

          // Card achtergrondkleur per status
          const cardBg = regel.status === 'GOEDGEKEURD'
            ? 'bg-green-50 border-green-200'
            : regel.status === 'BETWIST'
              ? 'bg-red-50 border-red-200'
              : '';

          return (
            <Card key={regel.id} className={clsx('overflow-hidden', cardBg)} padding={false}>
              {/* Regel header - always visible */}
              <button
                onClick={() => setExpandedRegel(isExpanded ? null : regel.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-black/[0.02] transition-colors"
              >
                <div className="flex-1 min-w-0 mr-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{regel.categorie}</span>
                    {regel.status === 'GOEDGEKEURD' && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        Goedgekeurd
                      </span>
                    )}
                    {regel.status === 'BETWIST' && (
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        Betwist
                      </span>
                    )}
                    {heeftFotos && (
                      <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                        <ImageIcon size={12} /> {regel.fotoUrls.length}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{regel.omschrijving}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-900">{formatValuta(regel.bedrag)}</span>
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  {/* Toelichting van de chauffeur */}
                  {regel.toelichting && (
                    <p className="text-sm text-gray-500 italic mb-3">"{regel.toelichting}"</p>
                  )}

                  {/* Foto's */}
                  {heeftFotos ? (
                    <div className="flex gap-2 flex-wrap mb-4">
                      {regel.fotoUrls.map((url, i) => (
                        <button
                          key={i}
                          onClick={() => { setLightboxImages(regel.fotoUrls); setLightboxIndex(i); }}
                          className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-gold transition-colors group"
                        >
                          <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ZoomIn size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">Geen foto's bijgevoegd</p>
                  )}

                  {/* Action buttons */}
                  {!isAfgerond && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          variant={regel.status === 'GOEDGEKEURD' ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => handleGoedkeuren(regel.id)}
                          disabled={regelMutation.isPending}
                          className={regel.status === 'GOEDGEKEURD' ? '!bg-green-600 hover:!bg-green-700' : ''}
                        >
                          <Check size={14} /> Goedkeuren
                        </Button>
                        <Button
                          variant={regel.status === 'BETWIST' ? 'danger' : 'secondary'}
                          size="sm"
                          onClick={() => handleBetwistStart(regel.id)}
                          disabled={regelMutation.isPending}
                        >
                          <X size={14} /> Betwisten
                        </Button>
                      </div>

                      {/* Betwist reden tekstveld */}
                      {isBetwistInputOpen && (
                        <div className="flex gap-2 items-start">
                          <textarea
                            value={betwistInput[regel.id] || ''}
                            onChange={(e) => setBetwistInput(prev => ({ ...prev, [regel.id]: e.target.value }))}
                            placeholder="Reden van betwisting..."
                            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold resize-none"
                            rows={2}
                            autoFocus
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleBetwistConfirm(regel.id)}
                            disabled={regelMutation.isPending}
                          >
                            Bevestig
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Sticky summary bar */}
      {!isAfgerond && (
        <div className="fixed bottom-[var(--k-voet)] left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="max-w-3xl mx-auto px-6 py-4">
            {/* Bedrag samenvatting */}
            <div className="flex items-center justify-between mb-3">
              <div className="grid grid-cols-3 gap-4 flex-1 mr-4">
                <div>
                  <p className="text-[11px] font-medium text-gray-500 uppercase">Goedgekeurd</p>
                  <p className="text-sm font-bold text-green-600">{formatValuta(goedgekeurdBedrag)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-gray-500 uppercase">Betwist</p>
                  <p className="text-sm font-bold text-red-600">{formatValuta(betwistBedrag)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-gray-500 uppercase">Nog te beoordelen</p>
                  <p className="text-sm font-bold text-gray-600">{formatValuta(pendingBedrag)}</p>
                </div>
              </div>

              {/* Afrond knoppen */}
              {alleBeoordeeld && alleGoedgekeurd && (
                <Button
                  onClick={() => goedkeurenMutation.mutate()}
                  disabled={goedkeurenMutation.isPending}
                  className="!bg-green-600 hover:!bg-green-700 whitespace-nowrap"
                >
                  {goedkeurenMutation.isPending ? 'Bezig...' : 'Vorderingsstaat Goedkeuren'}
                </Button>
              )}
              {alleBeoordeeld && !alleGoedgekeurd && (
                <Button
                  onClick={() => betwistenMutation.mutate()}
                  disabled={betwistenMutation.isPending}
                  className="!bg-amber-500 hover:!bg-amber-600 whitespace-nowrap"
                >
                  {betwistenMutation.isPending ? 'Bezig...' : 'Vorderingsstaat Betwisten'}
                </Button>
              )}
              {!alleBeoordeeld && (
                <Button disabled className="whitespace-nowrap">
                  {pendingCount} regel{pendingCount !== 1 ? 's' : ''} open
                </Button>
              )}
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gold rounded-full transition-all"
                style={{ width: `${regels.length > 0 ? (beoordeeld / regels.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={() => setLightboxImages([])}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxImages([]); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X size={24} />
          </button>
          {lightboxImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length); }}
                className="absolute left-4 text-white/80 hover:text-white text-4xl font-light"
              >
                &#8249;
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % lightboxImages.length); }}
                className="absolute right-12 text-white/80 hover:text-white text-4xl font-light"
              >
                &#8250;
              </button>
            </>
          )}
          <img
            src={lightboxImages[lightboxIndex]}
            alt="Foto"
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 text-white/60 text-sm">
            {lightboxIndex + 1} / {lightboxImages.length}
          </div>
        </div>
      )}
    </div>
  );
}
